/**
 * /dashboard/offboarding/$id — Offboarding cycle detail (G4.6 + G4.7).
 *
 * Layout (plan §7 — single column, stacked sections):
 *   1. Header strip — employee, status, reason, LWD, notice, initiator
 *   2. State-machine action panel — legal next states + close button
 *   3. Task checklist — per-task status + assignee
 *   4. ICC seat handoff (conditional — when icc_seat_handoff task exists)
 *   5. F&F compute panel — pre-eligible card / inputs form / immutable
 *      snapshot view (G4.7), then the F&F's books status ("Post to
 *      books" for an F&F computed while Accounting was off)
 *   6. Cycle letter library — generate relieving / experience / FF
 *      statement letters, send, download (G4.7)
 *
 * Read-only for an HR viewer the exit guard refuses (QA re-test 2026-10):
 * their OWN exit (self_exit_change) or, for an HR manager, a company
 * admin's (company_admin_protected). GET /cycles/{id} says so up front
 * (actions_blocked); every action would answer 403, so none is offered.
 *
 * Migration note (Phase 3f — 2026-06-01): port of
 * frontend/src/app/(dashboard)/dashboard/offboarding/[id]/page.tsx.
 * Swaps:
 *   - "use client" dropped
 *   - use(params) (Next.js) → Route.useParams() (TanStack)
 *   - next/link Link → @tanstack/react-router Link
 */

import { createFileRoute, Link , useParams} from "@tanstack/react-router";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { toApiError } from "@/lib/api";
import { useOffboardingCycle } from "@/hooks/use-offboarding";
import { useDirectory } from "@/hooks/use-directory";
import { HrModuleShell } from "@/components/shared/hr-module-shell";
import { OffboardingRoleGate } from "@/components/offboarding/offboarding-role-gate";
import { CycleHeader } from "@/components/offboarding/cycle-header";
import { StateMachineActions } from "@/components/offboarding/state-machine-actions";
import { TaskChecklist } from "@/components/offboarding/task-checklist";
import { ICCHandoffPanel } from "@/components/offboarding/icc-handoff-panel";
import { FFComputePanel } from "@/components/offboarding/ff-compute-panel";
import { FFBooksControl } from "@/components/offboarding/ff-books-control";
import { OutstandingAssetsPanel } from "@/components/offboarding/outstanding-assets-panel";
import { CycleLetterLibrary } from "@/components/offboarding/cycle-letter-library";
import { HandoverPanel } from "@/components/offboarding/handover-panel";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { statusLabel } from "@/lib/offboarding-format";
import { sentence } from "@/lib/offboarding-rules";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/hr/offboarding/$id")({
  component: OffboardingCycleDetailRoute,
});

function DetailSkeleton() {
  return (
    <div className="space-y-4">
      <Skeleton className="h-40 w-full" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}

function OffboardingCycleDetailRoute() {
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug: string };
  const { id } = Route.useParams();
  const numId = Number(id);
  const validId = Number.isFinite(numId) && numId > 0;
  const q = useOffboardingCycle(numId, validId);

  const cycle = q.data?.cycle;
  const tasks = q.data?.tasks ?? [];
  const iccHandoffTask = tasks.find(
    (t) => t.task_type === "icc_seat_handoff",
  );
  // The exit guard refuses this viewer every action on this exit.
  const blocked = q.data?.actions_blocked;
  const ended = cycle?.status === "closed" || cycle?.status === "cancelled";
  const readOnly = ended || !!blocked;
  // The leaver's name is the page title (breadcrumb: Exits › Meera Joshi).
  const dir = useDirectory({ includeInactive: true });
  const leaver = cycle && dir.byId.has(cycle.employee_id) ? dir.label(cycle.employee_id, null, false) : null;

  return (
    <HrModuleShell
      title={leaver ?? "Exit"}
      description={cycle ? `Status: ${statusLabel(cycle.status)}` : undefined}
      actions={
        <Button asChild variant="outline" className="gap-1.5 lg:hidden">
          <Link to="/app/$tenantSlug/hr/offboarding" params={{ tenantSlug }}>
            <ArrowLeft aria-hidden className="h-4 w-4" />
            Back
          </Link>
        </Button>
      }
    >
      <OffboardingRoleGate>
      {!validId && (
        <Alert variant="destructive">
          <AlertTitle>Invalid cycle id</AlertTitle>
          <AlertDescription>
            The URL doesn&apos;t look like a valid cycle id.
          </AlertDescription>
        </Alert>
      )}

      {validId && q.isLoading && <DetailSkeleton />}

      {validId && q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Could not load this cycle</AlertTitle>
          <AlertDescription>
            {toApiError(q.error).message}{" "}
            <Link
              to="/app/$tenantSlug/hr/offboarding" params={{ tenantSlug }}
              className="ml-1 inline-flex items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
            >
              Back to Exits
              <ChevronRight aria-hidden className="h-4 w-4" />
            </Link>
          </AlertDescription>
        </Alert>
      )}

      {validId && cycle && (
        <div className="space-y-5">
          {blocked && (
            <Alert data-testid="cycle-actions-blocked">
              <AlertTitle>
                {blocked.code === "self_exit_change"
                  ? "This is your own exit"
                  : "Only a company admin can work on this exit"}
              </AlertTitle>
              <AlertDescription>
                {sentence(blocked.message)} You can see where it stands, but
                not change it.
              </AlertDescription>
            </Alert>
          )}

          <CycleHeader cycle={cycle} readOnly={!!blocked} />

          {!blocked && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Actions</CardTitle>
              </CardHeader>
              <CardContent>
                <StateMachineActions cycle={cycle} tasks={tasks} />
              </CardContent>
            </Card>
          )}

          {/* A3: what must come back before the close gate lets go. */}
          <OutstandingAssetsPanel
            cycleId={numId}
            assets={q.data?.outstanding_assets ?? []}
            readOnly={!!blocked}
          />

          <TaskChecklist tasks={tasks} readOnly={readOnly} leaverId={cycle.employee_id} />

          {/* Who takes the leaver's ICC seat is HR's call — the server
              withholds the hand-off details from the leaver (QA re-test #2),
              so their own exit shows the task in the checklist only. */}
          {iccHandoffTask && blocked?.code !== "self_exit_change" && (
            <ICCHandoffPanel
              task={iccHandoffTask}
              exitingEmployeeId={cycle.employee_id}
              seatedUserIds={q.data?.icc_member_user_ids}
              readOnly={readOnly}
            />
          )}

          {/* Wave 2: reports / reviews / requisitions still pointing at the
              leaver — direct reports block the close. */}
          <HandoverPanel
            cycleId={numId}
            leaverId={cycle.employee_id}
            handover={q.data?.handover}
            readOnly={readOnly}
          />

          <FFComputePanel
            cycle={cycle}
            effectiveSnapshot={q.data?.effective_ff_snapshot}
            revisions={q.data?.ff_revisions ?? []}
            readOnly={!!blocked}
          />

          {/* The F&F settlement journal: posted at compute with
              Accounting on; an F&F computed while it was off gets an
              explicit, previewed "Post to books". Renders nothing when
              Accounting is off or the F&F isn't computed yet. Nobody
              books their own F&F (the server refuses it). */}
          {blocked?.code !== "self_exit_change" && (
            <FFBooksControl cycleId={numId} tenantSlug={tenantSlug} />
          )}

          <CycleLetterLibrary cycle={cycle} readOnly={!!blocked} />
        </div>
      )}
      </OffboardingRoleGate>
    </HrModuleShell>
  );
}
